/** biome-ignore-all lint/style/useNamingConvention: variant keys must match Optimizely flag configuration */
'use client';

import { type ReactNode, use } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import { ArticleSectionRelated } from '@/app_components/ArticleSections/ArticleSectionRelated';
import { AbTest } from '@/app_components/common/AbTest';
import type { Classification } from '@/app_lib/types/Article';
import type { ArticlePreview } from '@/app_lib/types/ArticlePreview';
import type { EventData } from '@/app_lib/types/Gtm';
import { getLatestHeading } from '@/app_lib/utils/sharedUtils';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';

const mostPopularTrackingData: EventData = {
  contentPlacementCD: 'most popular - bottom',
  eventAction: 'most popular module click',
  eventCategory: 'navigation',
};

const recircExperiment = tv({
  variants: {
    variant: {
      density_8: '',
      list: '',
      merged: 'grid grid-cols-1 md:grid-cols-2 md:grid-rows-[repeat(4,auto)] layout-grid-gap',
      merged_list: 'grid grid-cols-1 md:grid-cols-2 md:grid-rows-[repeat(11,auto)] layout-grid-gap',
      merged_mixed: 'grid grid-cols-1 md:grid-cols-2 layout-grid-gap',
      off: '',
    },
  },
});

type RecircVariant = NonNullable<VariantProps<typeof recircExperiment>['variant']>;

interface SectionConfig {
  className?: string;
  flat?: boolean;
  headingClassName?: string;
  layout?: 'grid-2col-mobile-2col-desktop' | 'grid-3col' | 'grid-4col' | 'list';
  maxArticles: number;
}

const VARIANT_CONFIGS: Record<RecircVariant, { latest: SectionConfig; popular: SectionConfig }> = {
  density_8: {
    latest: { layout: 'grid-4col', maxArticles: 8 },
    popular: { layout: 'grid-4col', maxArticles: 8 },
  },
  list: {
    latest: { layout: 'list', maxArticles: 6 },
    popular: { layout: 'list', maxArticles: 6 },
  },
  merged: {
    latest: {
      className: 'md:grid md:grid-cols-2 md:[grid-template-rows:subgrid] md:[grid-row:1/-1] md:gap-6',
      flat: true,
      headingClassName: 'md:col-span-2',
      layout: 'grid-2col-mobile-2col-desktop',
      maxArticles: 6,
    },
    popular: {
      className: 'md:grid md:grid-cols-2 md:[grid-template-rows:subgrid] md:[grid-row:1/-1] md:gap-6',
      flat: true,
      headingClassName: 'md:col-span-2',
      layout: 'grid-2col-mobile-2col-desktop',
      maxArticles: 6,
    },
  },
  merged_list: {
    latest: {
      className: 'md:grid md:[grid-template-rows:subgrid] md:[grid-row:1/-1] md:gap-6',
      flat: true,
      layout: 'list',
      maxArticles: 10,
    },
    popular: {
      className: 'md:grid md:[grid-template-rows:subgrid] md:[grid-row:1/-1] md:gap-6',
      flat: true,
      layout: 'list',
      maxArticles: 10,
    },
  },
  merged_mixed: {
    latest: { layout: 'grid-2col-mobile-2col-desktop', maxArticles: 8 },
    popular: { layout: 'list', maxArticles: 10 },
  },
  off: {
    latest: { maxArticles: 6 },
    popular: { maxArticles: 6 },
  },
};

interface RecirculationExperimentProps {
  latestArticlesPromise: Promise<ArticlePreview[]>;
  mostPopularPromise: Promise<ArticlePreview[]>;
  outbrainSlot?: ReactNode;
  section: Classification['primarySection'];
}

export const RecirculationExperiment = ({
  latestArticlesPromise,
  mostPopularPromise,
  outbrainSlot,
  section,
}: RecirculationExperimentProps) => {
  const latestArticles = use(latestArticlesPromise);
  const mostPopularArticles = use(mostPopularPromise);
  const heading = getLatestHeading(section);
  // TODO: Remove once promo_recirc_card experiment concludes
  const { userFlags } = useOptimizely();
  const recircPromoEnable = (userFlags?.promo_recirc_card as { variant: string })?.variant === 'on';

  const renderVariant = (variant: RecircVariant): ReactNode => {
    const { latest, popular } = VARIANT_CONFIGS[variant];
    const wrapperClass = recircExperiment({ variant });
    /** @experiment promo_recirc_card — with recirc_layout_test, the promo is not designed for all designs,
     * but works with the following. Remove variant check once recirc_layout_test concludes
     */
    const showRecircPromo = recircPromoEnable && (variant === 'density_8' || variant === 'merged' || variant === 'off');
    const recirculationCtrTracking = { name: 'recirculation_ctr' };
    const sections = (
      <>
        <ArticleSectionRelated
          articles={latestArticles}
          heading={heading}
          optimizelyTrackingData={recirculationCtrTracking}
          showRecircPromo={showRecircPromo}
          {...latest}
        />
        {outbrainSlot && variant === 'off' && (
          <AbTest flagName='swap_outbrain_recirc'>
            <div className='w-full'>
              <div className='container-content mx-auto'>{outbrainSlot}</div>
            </div>
          </AbTest>
        )}
        <ArticleSectionRelated
          articles={mostPopularArticles}
          heading='Most Popular'
          optimizelyTrackingData={recirculationCtrTracking}
          showRecircPromo={showRecircPromo}
          trackingData={mostPopularTrackingData}
          {...popular}
        />
      </>
    );

    return wrapperClass ? <div className={wrapperClass}>{sections}</div> : sections;
  };

  return (
    <AbTest
      defaultVariant='off'
      flagName='recirc_layout_test'
      variants={{
        density_8: renderVariant('density_8'),
        list: renderVariant('list'),
        merged: renderVariant('merged'),
        merged_list: renderVariant('merged_list'),
        merged_mixed: renderVariant('merged_mixed'),
        off: renderVariant('off'),
      }}
    />
  );
};
